import React from 'react';

import { Button } from '@mui/material';

import { Tuple } from '@clearverify/types';

import { PageLayout, TabItem, Tabs } from 'src/components/common';
import {
  DashboardFinancialCVAdmin,
  DashboardGlobalCVAdmin,
  DashboardVerifiersCVAdmin,
  PrintViewContainer,
} from 'src/components/dashboard';
import { ModalId } from 'src/constants/modals';
import { useCurrentPlatformStaff } from 'src/hooks/platformStaff';
import { useModalV2, useSearchParamsTabs } from 'src/hooks/shared';
import { paperStyles } from 'src/layouts/InboxLayout/styles';
import { DashboardYTDFinancialReportModal } from 'src/modals/dashboard';
import { DashboardCVAdminProvider } from 'src/providers/DashboardCVAdminProvider';
import { DashboardPrintViewProvider } from 'src/providers/DashboardPrintViewProvider';
import { DashboardTab } from 'src/types/dashboard';
import { SxProp } from 'src/types/mui';

export const DashboardPage: React.FC = () => {
  const { openModal } = useModalV2();
  const { currentTabKey, handleTabChange } = useSearchParamsTabs(TABS);

  const [isOpenPrintContainer, setOpenPrintContainerState] = React.useState(false);

  const { isFinancialReportAvailable } = useCurrentPlatformStaff();

  return (
    <React.Fragment>
      <PageLayout
        titleContent="Dashboard"
        topRightContent={
          <React.Fragment>
            {isFinancialReportAvailable && (
              <Button
                variant="outlined"
                onClick={() =>
                  openModal(DashboardYTDFinancialReportModal, {
                    id: ModalId.DashboardYTDFinancialReport,
                  })
                }
              >
                Generate report
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={() => setOpenPrintContainerState(true)}
              disabled={!EXPORTABLE_TABS.includes(currentTabKey)}
            >
              Export page
            </Button>
          </React.Fragment>
        }
        paperSx={paperStyles}
        topRightContentSx={topRightContentStyles}
      >
        <DashboardCVAdminProvider>
          <Tabs
            tabs={TABS}
            currentTabKey={currentTabKey}
            additionalTabsContainerStyles={{ margin: 0 }}
            onChange={handleTabChange}
          />
          {isOpenPrintContainer && (
            <PrintViewContainer onClose={() => setOpenPrintContainerState(false)}>
              <DashboardPrintViewProvider isPrintView>
                {currentTabKey === DashboardTab.Global && <DashboardGlobalCVAdmin />}
                {currentTabKey === DashboardTab.Verifiers && <DashboardVerifiersCVAdmin />}
                {currentTabKey === DashboardTab.Financial && <DashboardFinancialCVAdmin />}
              </DashboardPrintViewProvider>
            </PrintViewContainer>
          )}
        </DashboardCVAdminProvider>
      </PageLayout>
    </React.Fragment>
  );
};

const TABS: Tuple<TabItem<DashboardTab>, 3> = [
  {
    key: DashboardTab.Global,
    label: DashboardTab.Global,
    component: <DashboardGlobalCVAdmin />,
  },
  {
    key: DashboardTab.Verifiers,
    label: DashboardTab.Verifiers,
    component: <DashboardVerifiersCVAdmin />,
  },
  {
    key: DashboardTab.Financial,
    label: DashboardTab.Financial,
    component: <DashboardFinancialCVAdmin />,
  },
];

const EXPORTABLE_TABS: string[] = [
  DashboardTab.Global,
  DashboardTab.Verifiers,
  DashboardTab.Financial,
];

const topRightContentStyles: SxProp = {
  display: 'flex',
  gap: 2,
  alignItems: 'center',
};
